import { customPalette } from '../../../theme';

/** Set as the Syncfusion grid's own element id in ReportsTable.tsx. */
export const REPORTS_GRID_ID = 'reportsGrid';

/** Marker class on NoCollateralWarningIcon; the grid-scoped stylesheet below colours it. */
export const NO_COLLATERAL_WARNING_ICON_CLASS = 'no-collateral-warning-icon';

/** Bulk edit Cancel toolbar item id; ReportsBulkEditGrid focuses it on create. */
export const BULK_EDIT_CANCEL_TOOLBAR_ITEM_ID = 'bulkEditCancel';

/**
 * Syncfusion's `resetDialogAppend` (ej2-grids) moves the Column Chooser and Excel filter dialogs out of
 * the grid element and into a `div.e-grid-popup` it inserts as `document.body.firstChild`, whenever the
 * grid's content area is shorter than the dialog -- which happens on small laptops, at high browser zoom,
 * and after filtering down to a short result set. It names that wrapper `<gridId>_e-popup`, so pinning the
 * grid's own id above makes this second home a selector we can target rather than a generated string.
 */
export const REPORTS_GRID_POPUP_ID = `${REPORTS_GRID_ID}_e-popup`;

/**
 * Syncfusion computes the chooser's left edge against a hardcoded `dlgWidth = 250` internally, while this
 * stylesheet forces a different width -- leaving the dialog offset from where Syncfusion believes it is,
 * and drifting its connecting arrow. Exported so the positioning code uses the same number this CSS does
 * instead of a second, independently-maintained copy.
 */
export const COLUMN_CHOOSER_WIDTH_PX = 280;

/**
 * Helper function to generate flat CSS from nested template. Replaces `& ` with the provided root selector.
 */
const generateRootScopedCss = (rootSelector: string): string => {
    return `
    /* Column Chooser popup: mirrors the prototype's Fluent Menu popover (surface/search/list/footer)
       exactly, since Syncfusion renders this as its own dialog theme by default. Selectors are kept at
       the same depth as the imported corptax.fluent theme's rules (.e-grid .e-ccdlg ...) with !important
       so these win regardless of source order -- see the .e-toolbar .e-toolbar-item .e-tbar-btn rule
       below for the same specificity fight. */
    ${rootSelector} .e-ccdlg.e-dialog {
        width: ${COLUMN_CHOOSER_WIDTH_PX}px !important;
        max-width: ${COLUMN_CHOOSER_WIDTH_PX}px !important;
        max-height: calc(100vh - 16px) !important;
        border: 1px solid var(--colorNeutralStroke1) !important;
        border-radius: var(--borderRadiusMedium) !important;
        box-shadow: var(--shadow16) !important;
        background-color: var(--colorNeutralBackground1) !important;
        overflow: visible !important;
    }

    /* display:flex is scoped to .e-popup-open only -- Syncfusion closes this dialog purely via a
       CSS class (toggling between e-popup-open/e-popup-close, no inline display:none), and an
       unconditional display:flex here would win over that class-based hide via specificity,
       making the dialog impossible to close. Confirmed live: without this scoping, clicking
       OK/Cancel correctly adds e-popup-close to the element but it stays visually on screen. */
    ${rootSelector} .e-ccdlg.e-dialog.e-popup-open {
        display: flex !important;
        flex-direction: column !important;
    }

    /* Fluent's Menu popover renders a small arrow connecting the surface back to its trigger button
       (Menu's withArrow prop in the prototype) -- Syncfusion's dialog has no equivalent, so it's added here. */
    ${rootSelector} .e-ccdlg.e-dialog::before {
        content: '';
        position: absolute;
        top: -6px;
        right: 60px;
        width: 12px;
        height: 12px;
        background-color: var(--colorNeutralBackground1);
        border-top: 1px solid var(--colorNeutralStroke1);
        border-left: 1px solid var(--colorNeutralStroke1);
        border-top-left-radius: var(--borderRadiusSmall);
        transform: rotate(45deg);
    }

    ${rootSelector} .e-ccdlg .e-dlg-header-content {
        display: none !important;
    }

    /* This sits directly inside .e-ccdlg.e-dialog.e-popup-open (the flex column clamped to the
       viewport above) but Syncfusion ships it as display:block with min-height:auto -- per flexbox's
       automatic-minimum-size rule a block-level flex item refuses to shrink below its content's natural
       height unless told min-height: 0, so the checklist below kept growing past the dialog's own clamp
       and off the bottom of the viewport. Confirmed live at 200% zoom: the outer dialog box fit within a
       426px-tall viewport while .e-cc-contentdiv and .e-footer-content rendered 106-122px below it,
       completely unreachable. Making this a flex column itself is also what lets .e-main-div /
       .e-cc-contentdiv below actually act on their own flex: 1 1 auto instead of it being inert. */
    ${rootSelector} .e-ccdlg .e-dlg-content {
        margin: 0 !important;
        /* No right padding here -- the checklist's scrollbar (inside .e-cc-contentdiv, a descendant of this
           element) needs to sit flush against the popup's right edge instead of being inset from it. The
           search box and list rows restore their own right inset individually below. */
        padding: var(--spacingHorizontalM) 0 0 var(--spacingHorizontalM) !important;
        overflow: visible !important;
        display: flex !important;
        flex-direction: column !important;
        min-height: 0 !important;
    }

    /* Wraps the search box + checklist inside .e-dlg-content. Syncfusion again ships this block-level with
       min-height: 0 but no flex participation at all -- flex: 1 1 auto is inert on a non-flex-item, so the
       clamp from .e-dlg-content above never reached .e-cc-contentdiv until this element itself became a
       flex container the same way. */
    ${rootSelector} .e-ccdlg .e-main-div {
        display: flex !important;
        flex-direction: column !important;
        min-height: 0 !important;
        flex: 1 1 auto !important;
    }

    /* Matches the Figma reference (node 24582:20168): a real Fluent v9 Input with the search icon
       trailing, not leading -- per Fluent's own updated guidance against a leading/"right leaning"
       magnifying glass. Syncfusion already renders the icon <span> after the <input> in the DOM, so no
       reordering is needed; the inner gap is the design's own measured value, tightened down from the
       design's own 10px side padding since that read as too much breathing room against the border. */
    ${rootSelector} .e-ccdlg .e-cc-searchdiv {
        /* Syncfusion defaults this to position:absolute with top/left/right offsets. Keep relative so
           the Fluent focus ::after can anchor, but clear those offsets so it stays in normal flow
           inside the popover (static was used before; relative + auto offsets is equivalent for layout). */
        position: relative !important;
        top: auto !important;
        left: auto !important;
        right: auto !important;
        opacity: 1 !important;
        display: flex !important;
        align-items: center !important;
        gap: 10px;
        width: calc(100% - var(--spacingHorizontalM)) !important;
        min-height: 32px;
        box-sizing: border-box;
        background-color: var(--colorNeutralBackground1);
        border: 1px solid var(--colorNeutralStroke1) !important;
        border-bottom-color: var(--colorNeutralStrokeAccessible) !important;
        border-radius: var(--borderRadiusMedium) !important;
        padding: 0 var(--spacingHorizontalS) !important;
        margin-right: var(--spacingHorizontalM) !important;
        margin-bottom: var(--spacingHorizontalS) !important;
        outline: none !important;
        box-shadow: none !important;
    }

    /* Hide Syncfusion's underline chrome; Fluent Input focus uses our ::after below. */
    ${rootSelector} .e-ccdlg .e-cc-searchdiv::before {
        display: none !important;
        content: none !important;
        width: 0 !important;
        background: none !important;
    }

    /* Fluent Input / SearchBox medium focus underline (scaleX brand stroke on focus-within). */
    ${rootSelector} .e-ccdlg .e-cc-searchdiv::after {
        box-sizing: border-box !important;
        content: '' !important;
        position: absolute !important;
        left: -1px !important;
        bottom: -1px !important;
        right: -1px !important;
        width: auto !important;
        height: max(2px, var(--borderRadiusMedium)) !important;
        border: none !important;
        border-bottom-left-radius: var(--borderRadiusMedium);
        border-bottom-right-radius: var(--borderRadiusMedium);
        border-bottom: 2px solid var(--colorCompoundBrandStroke) !important;
        clip-path: inset(calc(100% - 2px) 0 0 0);
        transform: scaleX(0);
        transition-property: transform;
        transition-duration: var(--durationUltraFast);
        transition-delay: var(--curveAccelerateMid);
        background: none !important;
        pointer-events: none;
    }

    ${rootSelector} .e-ccdlg .e-cc-searchdiv:hover {
        border-color: var(--colorNeutralStroke1Hover) !important;
        border-bottom-color: var(--colorNeutralStrokeAccessibleHover) !important;
        box-shadow: none !important;
    }

    /* Kill Syncfusion fluent2's full primary (blue) border on .e-input-focus -- Fluent only thickens
       the bottom via ::after, and keeps side borders at pressed neutrals. */
    ${rootSelector} .e-ccdlg .e-cc-searchdiv:active,
    ${rootSelector} .e-ccdlg .e-cc-searchdiv:focus-within,
    ${rootSelector} .e-ccdlg .e-cc-searchdiv.e-input-focus {
        outline: none !important;
        box-shadow: none !important;
        border: 1px solid var(--colorNeutralStroke1Pressed) !important;
        border-bottom-color: var(--colorNeutralStrokeAccessiblePressed) !important;
    }

    ${rootSelector} .e-ccdlg .e-cc-searchdiv:focus-within::after,
    ${rootSelector} .e-ccdlg .e-cc-searchdiv.e-input-focus::after {
        transform: scaleX(1);
        width: auto !important;
        background: none !important;
        transition-property: transform;
        transition-duration: var(--durationNormal);
        transition-delay: var(--curveDecelerateMid);
    }

    ${rootSelector} .e-ccdlg .e-cc-searchdiv:focus-within:active::after {
        border-bottom-color: var(--colorCompoundBrandStrokePressed);
    }

    @media screen and (prefers-reduced-motion: reduce) {
        ${rootSelector} .e-ccdlg .e-cc-searchdiv::after,
        ${rootSelector} .e-ccdlg .e-cc-searchdiv:focus-within::after,
        ${rootSelector} .e-ccdlg .e-cc-searchdiv.e-input-focus::after {
            transition-duration: 0.01ms;
            transition-delay: 0.01ms;
        }
    }

    /* Syncfusion's own input-group-icon theme fixes this span at 30x30 via min-width/min-height (which
       wins over a plain width/height on a flex item), so the glyph sat flush-left inside a wider box --
       reading as an empty gap on the right, since the box (not the glyph) is what actually touches the
       container's edge. Overriding the min- properties too removes that dead space instead of just
       centering the glyph within it. Sized to match the plain Fluent SearchBox used by the column filters
       (FilterSearchBar) rather than Syncfusion's smaller default glyph, so both search icons read the same. */
    ${rootSelector} .e-ccdlg .e-cc-searchdiv .e-ccsearch-icon {
        float: none !important;
        opacity: 1 !important;
        padding: 0 !important;
        margin: 0 !important;
        width: 24px !important;
        height: 24px !important;
        min-width: 24px !important;
        min-height: 24px !important;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colorNeutralForeground3);
        font-size: 14px;
    }

    ${rootSelector} .e-ccdlg .e-cc-searchdiv .e-ccsearch {
        flex: 1;
        min-width: 0;
        border: none !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        color: var(--colorNeutralForeground1);
        font-family: var(--fontFamilyBase);
        font-size: var(--fontSizeBase300);
    }

    ${rootSelector} .e-ccdlg .e-cc-searchdiv .e-ccsearch::placeholder {
        color: var(--colorNeutralForeground4);
        opacity: 1;
    }

    ${rootSelector} .e-ccdlg .e-cc-contentdiv {
        height: auto !important;
        /* Was a flat 280px cap. Inside a dialog clamped to the viewport (above), a fixed cap makes the
           dialog clip its own list at high zoom instead of scrolling it -- min-height: 0 is what lets a
           flex child actually shrink below its content size. */
        max-height: 280px !important;
        min-height: 0 !important;
        flex: 1 1 auto !important;
        overflow-y: auto !important;
        padding: 0 !important;
    }

    ${rootSelector} .e-ccdlg .e-ccul-ele {
        margin: 0 !important;
        padding: 0 !important;
    }

    ${rootSelector} .e-ccdlg li.e-cclist {
        padding: var(--spacingVerticalXS) var(--spacingHorizontalM) var(--spacingVerticalXS) 0 !important;
    }

    /* The prototype's checklist has no highlight for the last-focused row -- Syncfusion's theme shades it
       grey by default (li.e-cclist.e-colfocus), which this popup shouldn't carry over. */
    ${rootSelector} .e-ccdlg li.e-cclist.e-colfocus {
        background-color: transparent !important;
    }

    ${rootSelector} .e-ccdlg li.e-cclist.e-cc-selectall {
        border-bottom: 1px solid var(--colorNeutralStroke2);
        /* The divider is a border on this li's own box, which spans edge-to-edge regardless of padding --
           shifting the box inward with margin-right (instead of padding-right, which the general
           li.e-cclist rule already set) leaves a gap between the line and the scrollbar. */
        padding-right: 0 !important;
        margin-right: var(--spacingHorizontalM);
        margin-bottom: var(--spacingVerticalXS);
        padding-bottom: var(--spacingVerticalXS) !important;
    }

    /* Real Fluent Checkbox metrics (16px indicator, spacingVerticalS/spacingHorizontalS margins,
       borderRadiusSmall corners) scoped to the popup only -- the grid's own row-selection checkboxes
       keep their existing brand-colored, 6px-radius theme further below. */
    ${rootSelector} .e-ccdlg .e-checkbox-wrapper {
        display: flex !important;
        align-items: center !important;
        padding: 0 !important;
    }

    ${rootSelector} .e-ccdlg .e-checkbox-wrapper .e-frame {
        width: 16px !important;
        height: 16px !important;
        margin: var(--spacingVerticalS) var(--spacingHorizontalS) !important;
        border-radius: var(--borderRadiusSmall) !important;
        border-width: var(--strokeWidthThin) !important;
        border-color: var(--colorNeutralStrokeAccessible) !important;
        background-color: transparent !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
        font-size: 12px !important;
    }

    ${rootSelector} .e-ccdlg .e-checkbox-wrapper .e-frame.e-check {
        background-color: var(--colorCompoundBrandBackground) !important;
        border-color: var(--colorCompoundBrandBackground) !important;
        color: var(--colorNeutralForegroundInverted) !important;
    }

    /* The actual tab stop is the checkbox's own hidden input (.e-chk-hidden, opacity:0/1px per Syncfusion's
       theme) -- .e-frame is only its decorative sibling, rendered immediately after it in the DOM (see
       ColumnChooser.prototype's cBoxTrue/cBoxFalse setup in ej2-grids' column-chooser.js: the hidden input is
       inserted as the wrapper's firstChild, directly before .e-frame). Neither one shows any focus indicator
       on its own -- the input is invisible and .e-frame has no :focus-visible rule of its own -- so a keyboard
       user tabbing through the checklist has no way to tell which row is currently focused. Styling the frame
       via the adjacent-sibling combinator is what makes that focus visible on the real, visible checkbox. */
    ${rootSelector} .e-ccdlg .e-checkbox-wrapper .e-chk-hidden:focus-visible + .e-frame {
        outline: 2px solid ${customPalette.themePrimary} !important;
        outline-offset: 2px !important;
    }

    ${rootSelector} .e-ccdlg .e-cc-lab-name,
    ${rootSelector} .e-ccdlg .e-checkbox-wrapper .e-label {
        width: auto !important;
        padding: var(--spacingVerticalS) var(--spacingHorizontalS) !important;
        color: var(--colorNeutralForeground1);
        font-size: var(--fontSizeBase300);
        font-family: var(--fontFamilyBase);
    }

    ${rootSelector} .e-ccdlg .e-footer-content {
        display: flex !important;
        justify-content: flex-end !important;
        gap: var(--spacingHorizontalS);
        padding: var(--spacingHorizontalM) !important;
        border-top: 1px solid var(--colorNeutralStroke2) !important;
    }

    /* Real Fluent Button metrics (medium, secondary appearance) for OK/Cancel -- same base values as the
       toolbar buttons above, since Syncfusion's own e-btn theme sizes these completely differently. */
    ${rootSelector} .e-ccdlg .e-footer-content .e-btn {
        align-items: center !important;
        box-sizing: border-box !important;
        display: inline-flex !important;
        justify-content: center !important;
        margin: 0 !important;
        padding: 5px var(--spacingHorizontalM) !important;
        min-width: unset !important;
        height: auto !important;
        border-radius: var(--borderRadiusMedium) !important;
        font-size: var(--fontSizeBase300) !important;
        font-weight: var(--fontWeightSemibold) !important;
        line-height: var(--lineHeightBase300) !important;
        font-family: var(--fontFamilyBase) !important;
        border: 1px solid var(--colorNeutralStroke1) !important;
        background-color: var(--colorNeutralBackground1) !important;
        color: var(--colorNeutralForeground1) !important;
    }

    ${rootSelector} .e-ccdlg .e-footer-content .e-btn:hover {
        background-color: var(--colorNeutralBackground1Hover) !important;
        border-color: var(--colorNeutralStroke1Hover) !important;
    }

    ${rootSelector} .e-ccdlg .e-footer-content .e-cc_okbtn {
        background-color: var(--colorBrandBackground) !important;
        color: var(--colorNeutralForegroundOnBrand) !important;
        border-color: transparent !important;
    }

    ${rootSelector} .e-ccdlg .e-footer-content .e-cc_okbtn:hover {
        background-color: var(--colorBrandBackgroundHover) !important;
        border-color: transparent !important;
    }

    /* Excel-style column filter popup: needs the same visible overflow as the Column Chooser dialog above
       so its own arrow (added next) isn't clipped by the dialog's box. Syncfusion positions this dialog
       (see getFilterMenuPostion in ej2-grids) right up against the filter icon's bottom edge -- the extra
       top margin pushes the whole popup (arrow included) down a little so there's a visible gap between
       the arrow and the icon instead of the two touching. */
    ${rootSelector} .e-excelfilter.e-dialog {
        overflow: visible !important;
        margin-top: 8px !important;
        max-height: calc(100vh - 16px) !important;
    }

    /* Same arrow treatment as the Column Chooser popup above, connecting this popup back to the column's
       filter icon. ReportsTable.tsx repositions this dialog so its left edge lands under the filter icon
       (opening bottom-right of the icon) for most columns -- so a small, fixed offset from the popup's own
       left edge keeps the arrow aligned under the icon. For a column near the right edge of the screen,
       shifting right would push the dialog off screen, so ReportsTable.tsx leaves it in Syncfusion's
       original bottom-left position instead (marked via data-filter-dialog-anchor="left") -- the override
       below moves the same arrow back to the popup's right edge to match. */
    ${rootSelector} .e-excelfilter.e-dialog::before {
        content: '';
        position: absolute;
        top: -6px;
        left: 8px;
        width: 12px;
        height: 12px;
        background-color: var(--colorNeutralBackground1);
        border-top: 1px solid var(--colorNeutralStroke1);
        border-left: 1px solid var(--colorNeutralStroke1);
        border-top-left-radius: var(--borderRadiusSmall);
        transform: rotate(45deg);
        z-index: 1;
    }

    ${rootSelector} .e-excelfilter.e-dialog[data-filter-dialog-anchor='left']::before {
        left: auto;
        right: 1px;
    }

    /* Same broken link as .e-ccdlg .e-dlg-content above: this sits directly inside .e-excelfilter.e-dialog
       (a real flex column, clamped to the viewport above) but Syncfusion ships it display:block with
       min-height: auto and only overflow: hidden auto -- non-visible overflow alone doesn't help here,
       since .e-dlg-content still isn't a flex container itself, so its children (.e-searchcontainer,
       .e-spinner, .e-checkboxlist) are just stacked block content with no signal for which one should stay
       a fixed height and which should shrink/scroll. Confirmed live: the outer dialog fit a 426px-tall
       viewport while the checkbox list rendered top 438/bottom 638 and the OK button top 450.5/bottom
       482.5 -- both entirely below the viewport, on the Status column filter with no entity tabs involved. */
    ${rootSelector} .e-excelfilter .e-dlg-content {
        display: flex !important;
        flex-direction: column !important;
        min-height: 0 !important;
    }

    /* .e-searchcontainer is NOT fixed-height chrome -- it's the wrapper that contains everything below the
       search box (per ej2-grids' checkbox-filter-base.js / excel-filter-base.js: the spinner and checkbox
       list are appended inside it), so it's the actual flex item of .e-dlg-content and needs to grow/shrink
       with the available space like .e-main-div does for the Column Chooser above. Confirmed independently
       by tracing the installed @syncfusion/ej2-grids source and live in a running browser -- giving this
       element flex: 0 0 auto (an earlier attempt) hard-locked its entire nested subtree, including the
       checklist below, at its full natural height regardless of the dialog's own clamp. */
    ${rootSelector} .e-excelfilter .e-searchcontainer {
        display: flex !important;
        flex-direction: column !important;
        min-height: 0 !important;
        flex: 1 1 auto !important;
    }

    /* The actual fixed-height chrome is the search input itself, one level down from .e-searchcontainer --
       it should never be asked to shrink. */
    ${rootSelector} .e-excelfilter .e-searchbox {
        flex: 0 0 auto !important;
    }

    /* .e-spinner wraps the checklist (.e-checkboxlist below is its child, not its sibling) and ships as a
       plain block element, so .e-checkboxlist's own flex: 1 1 auto stays inert until this ancestor becomes
       a real flex container too -- same automatic-minimum-size chain as .e-dlg-content/.e-searchcontainer
       above. */
    ${rootSelector} .e-excelfilter .e-spinner {
        display: flex !important;
        flex-direction: column !important;
        min-height: 0 !important;
        flex: 1 1 auto !important;
    }

    /* Same reasoning as .e-cc-contentdiv above: the list, not the dialog, absorbs a clamped height. Needs
       flex: 1 1 auto (not Syncfusion's default) now that .e-spinner above is a real flex container, so
       this is the child that actually grows/shrinks to the remaining space and scrolls internally. */
    ${rootSelector} .e-excelfilter .e-checkboxlist {
        min-height: 0 !important;
        overflow-y: auto !important;
        flex: 1 1 auto !important;
    }

    /* .e-contextmenu-wrapper (the always-visible "Sort A to Z / Sort Z to A / Clear Filter / Text Filters"
       menu) and .e-footer-content (OK/Cancel) are real, always-rendered siblings of .e-dlg-content in the
       outer flex column -- measured live at ~149px and ~80px respectively. Without an explicit flex-basis
       they compete unpredictably with .e-dlg-content for the dialog's clamped height; pinning them to their
       natural size keeps that competition deterministic. Excel filter markup doesn't share the Column
       Chooser's .e-ccdlg .e-footer-content rule above, so it needs its own here. */
    ${rootSelector} .e-excelfilter .e-contextmenu-wrapper,
    ${rootSelector} .e-excelfilter .e-footer-content {
        flex: 0 0 auto !important;
    }

    /* Entity column filter: tab strip injected above the search box (see the MutationObserver-driven effect
       in ReportsTable.tsx) letting the user switch between filtering by individual entities or by entity
       groups. Both tabs draw from one combined checkbox list rather than two separately-loaded lists.
       Matches the prototype's Fluent TabList (small, underline-selected) look rather than a boxed/pill
       button pair -- text-only tabs with a brand-colored underline on the active one. */
    ${rootSelector} .e-excelfilter .entity-filter-tabs {
        display: flex;
        padding: 0 var(--spacingHorizontalM) var(--spacingVerticalXS);
    }

    ${rootSelector} .e-excelfilter .entity-filter-tab {
        flex: 0 0 auto;
        box-sizing: border-box;
        border: none;
        border-bottom: 2px solid transparent;
        background-color: transparent;
        color: var(--colorNeutralForeground2);
        font-family: var(--fontFamilyBase);
        font-size: var(--fontSizeBase200);
        font-weight: var(--fontWeightRegular);
        padding: var(--spacingVerticalS) var(--spacingHorizontalM);
        cursor: pointer;
    }

    ${rootSelector} .e-excelfilter .entity-filter-tab:hover {
        background-color: var(--colorSubtleBackgroundHover);
    }

    ${rootSelector} .e-excelfilter .entity-filter-tab[aria-selected='true'] {
        border-bottom-color: var(--colorCompoundBrandStroke);
        color: var(--colorNeutralForeground1);
        font-weight: var(--fontWeightSemibold);
    }

    ${rootSelector} .e-excelfilter .entity-filter-tab:focus-visible {
        outline: 2px solid ${customPalette.themePrimary};
        outline-offset: -2px;
    }

    /* Each checkbox row (a .e-ftrchk div, not a list item -- that's the Column Chooser's markup, not the
       Excel filter's) is tagged by the entity filter tabs effect in ReportsTable.tsx with whether it's an
       entity or an entity group -- these rules hide whichever half doesn't match the active tab. Rows
       Syncfusion owns itself (Select All, the search box's "add current selection" row) never carry the
       tag, so they stay visible regardless of which tab is active. */
    ${rootSelector} .e-excelfilter[data-entity-tab='entity'] .e-checkboxlist .e-ftrchk[data-entity-group='true'] {
        display: none !important;
    }

    ${rootSelector} .e-excelfilter[data-entity-tab='group'] .e-checkboxlist .e-ftrchk[data-entity-group='false'] {
        display: none !important;
    }
`;
};

const reportsGridDialogGlobalStylesText = (): string => {
    const gridRootCss = generateRootScopedCss(`#${REPORTS_GRID_ID}`);
    const popupRootCss = generateRootScopedCss(`#${REPORTS_GRID_POPUP_ID}`);

    return `${gridRootCss}

${popupRootCss}

/* Fluent's MenuTrigger keeps its button shaded while the menu it opens stays open -- Syncfusion's
   toolbar button has no such state, so it's driven off the popup's own open/close class. Two
   selectors because the dialog has two possible homes (see REPORTS_GRID_POPUP_ID): when it has been
   reparented onto document.body, a :has() scoped to the grid can no longer see it, so that case has
   to reach across from body instead. */
#${REPORTS_GRID_ID}:has(.e-ccdlg.e-popup-open) .e-toolbar .e-toolbar-item.e-cc-toolbar .e-tbar-btn,
body:has(#${REPORTS_GRID_POPUP_ID} .e-ccdlg.e-popup-open)
    #${REPORTS_GRID_ID} .e-toolbar .e-toolbar-item.e-cc-toolbar .e-tbar-btn {
    background-color: var(--colorSubtleBackgroundSelected) !important;
    color: var(--colorNeutralForeground2Selected) !important;
}

/* The corptax.fluent theme recolors every grid icon with \`.e-grid .e-icons:not(...)\` (0,3,0), which beats a Griffel
   class. Scoping to the grid's id outranks it without !important or the style prop. */
#${REPORTS_GRID_ID} .e-icons.${NO_COLLATERAL_WARNING_ICON_CLASS} {
    color: ${customPalette.goldenrod};
}`;
};

export const ReportsGridDialogGlobalStyles = () => <style>{reportsGridDialogGlobalStylesText()}</style>;
